import React, { useContext, useEffect, useState } from 'react'
import TokenUsageBar from './TokenUsageBar'
import PricingCards from './PricingCards'
import TokenActions from './TokenActions'
import apiV2 from "../../services/apiV2"
import { AuthContext } from '../../context/Auth/AuthContext'
import {
  connectSocket,
  addGoWebSocketMessageHandler,
  addGoWebSocketOpenHandler,
  getGoWebSocket,
} from "../../services/socketv2";



export default function AIManagerModal({ open, onClose, setTokens }) {
  const [tab, setTab] = useState('overview')
  const [planos, setPlans] = useState([])
  const [plano, setPlano] = useState({})
  const { user } = useContext(AuthContext);
  const [reload, setReload] = useState(false)
  const [paymentConfirmed, setPaymentConfirmed] = useState(false)

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const { data } = await apiV2.get("/plansToken");
        const tokens = await apiV2.get("/tokens");
        const companyPlan = tokens.data?.Plano ?? {};
        const monthlyTokens = companyPlan.monthlyTokens ?? 0;
        const extraTokens = companyPlan.extraTokens ?? 0;
        const usedTokens = companyPlan.usedTokens ?? 0;

        const calTotalTokens = monthlyTokens + extraTokens;

        setPlano(companyPlan);
        setPlans(Array.isArray(data?.Planos) ? data.Planos : []);
        setTokens([calTotalTokens, usedTokens]);
      } catch (err) {
        console.error(err);
        setPlano({});
        setPlans([]);
        setTokens([0, 0]);
      }
    };

    loadPlans();
  }, [reload]);

  useEffect(() => {
    if (!user?.id) return;

    const handleMessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (data.action === "paymentConfirmed") {
        setReload(prev => !prev);
        setPaymentConfirmed(true)
      }
    };

    const joinRoom = () => {
      const s = getGoWebSocket();
      if (!s || s.readyState !== WebSocket.OPEN) return;

      s.send(
        JSON.stringify({
          action: "joinPayment",
          room: String(user.companyId),
        })
      );
    };

    const removeMessage = addGoWebSocketMessageHandler(handleMessage);
    const removeOpen = addGoWebSocketOpenHandler(joinRoom);

    connectSocket({
      userId: user.id,
      companyId: user.companyId,
    });

    return () => {
      removeMessage();
      removeOpen();

      const s = getGoWebSocket();
      if (s && s.readyState === WebSocket.OPEN) {
        s.send(
          JSON.stringify({
            action: "leavePayment",
            room: String(user.companyId),
          })
        );
      }
    };
  }, [user]);


  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-3xl bg-[#0f1117] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.07]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-600/20 border border-violet-500/30 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1.5C8 1.5 3 4 3 8.5C3 11.26 5.24 13.5 8 13.5C10.76 13.5 13 11.26 13 8.5C13 4 8 1.5 8 1.5Z" stroke="#a78bfa" strokeWidth="1.2" strokeLinejoin="round" />
                <circle cx="8" cy="8.5" r="2" fill="#a78bfa" />
              </svg>
            </div>
            <div>
              <h2 className="text-white text-[15px] font-semibold leading-tight">AI Manager</h2>
              <p className="text-white/40 text-[11px] mt-0.5">Gerenciador de tokens e planos</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md hover:bg-white/10 flex items-center justify-center transition-colors group"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-white/40 group-hover:text-white/70 transition-colors" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4 border-b border-white/[0.07]">
          {(['overview', 'pricing']).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-[13px] font-medium rounded-t-md transition-colors capitalize pb-3 border-b-2 -mb-px ${tab === t
                ? 'text-violet-400 border-violet-500'
                : 'text-white/40 border-transparent hover:text-white/70'
                }`}
            >
              {t === 'overview' ? 'Visão Geral' : t === 'pricing' ? 'Planos' : 'Cobrança'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto scrollbar-hide">
          {tab === 'overview' && <OverviewTab companyPlan={plano} setTab={setTab} />}
          {tab === 'pricing' && <PricingCards plans={planos} paymentConfirmed={paymentConfirmed} setPaymentConfirmed={setPaymentConfirmed}
          />}
          {/* {tab === 'billing' && <TokenActions />} */}
        </div>
      </div>
    </div>
  )
}

function OverviewTab({ companyPlan, setTab }) {
  const plan = companyPlan ?? {};

  const totalTokens =
    (plan.monthlyTokens ?? 0) +
    (plan.extraTokens ?? 0);

  const used = plan.usedTokens ?? 0;
  const remaining = Math.max(0, totalTokens - used);

  const renewDays = plan.expiresAt
    ? Math.max(
        0,
        Math.ceil(
          (new Date(plan.expiresAt) - new Date()) /
            (1000 * 60 * 60 * 24)
        )
      )
    : 0;

  return (
    <div className="space-y-6">
      {/* Plano Atual */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/50 text-[11px] uppercase tracking-widest mb-1">
            Plano Atual
          </p>

          <div className="flex items-center gap-2">
            <span className="text-white text-xl font-bold">
              {plan.Plan?.name || "Sem plano"}
            </span>

            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                plan.status === "ACTIVE"
                  ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-400"
                  : "bg-red-500/20 border border-red-500/40 text-red-400"
              }`}
            >
              {plan.status || "INATIVO"}
            </span>
          </div>
        </div>

        <div className="text-right">
          <p className="text-white/50 text-[11px]">Renova em</p>

          <p className="text-white text-sm font-semibold">
            {renewDays} dias
          </p>
        </div>
      </div>

      <TokenUsageBar used={used} total={totalTokens} />

      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
          <p className="text-white/40 text-[11px]">Tokens do Plano</p>

          <p className="text-white text-lg font-bold">
            {(plan.monthlyTokens ?? 0).toLocaleString("pt-BR")}
          </p>
        </div>

        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
          <p className="text-white/40 text-[11px]">Tokens Extras</p>

          <p className="text-violet-400 text-lg font-bold">
            {(plan.extraTokens ?? 0).toLocaleString("pt-BR")}
          </p>
        </div>

        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
          <p className="text-white/40 text-[11px]">Tokens Disponíveis</p>

          <p className="text-emerald-400 text-lg font-bold">
            {remaining.toLocaleString("pt-BR")}
          </p>
        </div>

        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
          <p className="text-white/40 text-[11px]">Tokens usados</p>

          <p className="text-red-500 text-lg font-bold">
            {used.toLocaleString("pt-BR")}
          </p>
        </div>
      </div>

      <button
        className="w-full py-3 bg-violet-600 hover:bg-violet-500 text-white rounded-xl font-semibold transition"
        onClick={() => setTab("pricing")}
      >
        Comprar Tokens Adicionais
      </button>
    </div>
  );
}